import React, { useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Calendar } from "@/components/ui/calendar";
import { api, errMsg } from "@/lib/api";

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromISO = (s) => (s ? new Date(`${s.slice(0, 10)}T12:00:00`) : undefined);
const toUS = (s) => (s ? `${s.slice(5, 7)}/${s.slice(8, 10)}/${s.slice(0, 4)}` : "");
const parseUS = (s) => {
  const m = (s || "").trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const d = new Date(+m[3], +m[1] - 1, +m[2], 12);
  return d.getMonth() === +m[1] - 1 && d.getDate() === +m[2] ? toISO(d) : null;
};

function RemoveRxDialog({ open, setOpen, patient, value, onSaved, testid }) {
  const [linked, setLinked] = useState(0);
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/patients/${patient.id}/inline-date`, { field: "rx_date", remove: true, old_date: value, unlink_fills: linked > 0 });
      toast.success(`Rx date removed for ${patient.patient_id}${data.fills_unlinked ? ` — ${data.fills_unlinked} fill(s) kept and unlinked` : ""}`);
      setOpen(false);
      setLinked(0);
      onSaved();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 409 && det?.code === "linked_fills") setLinked(det.count);
      else toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setLinked(0); }}>
      <AlertDialogContent onClick={(e) => e.stopPropagation()} data-testid={`${testid}-remove-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove Prescription Date?</AlertDialogTitle>
          <AlertDialogDescription>Are you sure you want to remove this prescription date? This action will update the patient&apos;s prescription history. ({patient.patient_id} · {toUS(value)})</AlertDialogDescription>
        </AlertDialogHeader>
        {linked > 0 && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid={`${testid}-linked-warning`}>{linked} recorded fill(s) are linked to this prescription. They will be kept (and still counted) but no longer linked to it. Confirm to continue.</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid={`${testid}-remove-cancel`}>Cancel</AlertDialogCancel>
          <button className="btn h-10 bg-red-600 px-4 text-white hover:bg-red-700" disabled={busy} onClick={remove} data-testid={`${testid}-remove-confirm`}>{busy ? "Removing…" : linked ? "Keep Fills & Remove Date" : "Remove Date"}</button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function InlineDate({ patient, field, value, label, children, onSaved, testid }) {
  const [open, setOpen] = useState(false);
  const [rm, setRm] = useState(false);
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const iso = parseUS(txt);
  const openIt = (o) => { setOpen(o); if (o) setTxt(toUS(value)); };
  const save = async () => {
    if (!iso && !(field !== "rx_date" && !txt.trim())) return toast.error("Enter a valid date (MM/DD/YYYY)");
    setBusy(true);
    try {
      await api.post(`/patients/${patient.id}/inline-date`, { field, date: iso || null, old_date: field === "rx_date" ? value || null : undefined });
      toast.success(`${label} updated for ${patient.patient_id}`);
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const menu = field === "rx_date" && !!value;
  const trigger = (
    <button type="button" className="group inline-flex items-center gap-1 rounded px-0.5 hover:bg-[#EAF4FF] dark:hover:bg-white/5" onClick={(e) => e.stopPropagation()} data-testid={testid} title={`Edit ${label}`}>
      {children}<Pencil size={10} className="opacity-0 text-[#283F91] transition-opacity group-hover:opacity-70" />
    </button>
  );
  return (
    <Popover open={open} onOpenChange={openIt}>
      {menu ? (
        <PopoverAnchor asChild>
          <span>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
              <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()} onCloseAutoFocus={(e) => e.preventDefault()}>
                <DropdownMenuItem onSelect={() => setTimeout(() => openIt(true), 120)} data-testid={`${testid}-menu-edit`}><Pencil size={12} className="mr-2" />Edit Rx Date</DropdownMenuItem>
                <DropdownMenuItem className="text-red-600" onSelect={() => setTimeout(() => setRm(true), 0)} data-testid={`${testid}-menu-remove`}><Trash2 size={12} className="mr-2" />Remove Rx Date</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <RemoveRxDialog open={rm} setOpen={setRm} patient={patient} value={value} onSaved={onSaved} testid={testid} />
          </span>
        </PopoverAnchor>
      ) : <PopoverTrigger asChild>{trigger}</PopoverTrigger>}
      <PopoverContent className="w-auto p-0" align="start" onClick={(e) => e.stopPropagation()} onFocusOutside={(e) => e.preventDefault()} data-testid={`${testid}-popover`}>
        <div className="border-b px-3 py-2 text-xs font-semibold text-[#20336F] dark:text-blue-200">{label} · {patient.patient_id}</div>
        <Calendar mode="single" selected={fromISO(iso)} defaultMonth={fromISO(iso || value)} onSelect={(d) => d && setTxt(toUS(toISO(d)))} />
        <div className="flex items-center gap-2 border-t p-2">
          <input className="inp h-8 w-32 text-[13px]" placeholder="MM/DD/YYYY" value={txt} onChange={(e) => setTxt(e.target.value)} data-testid={`${testid}-input`} />
          <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)} disabled={busy} data-testid={`${testid}-cancel`}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={busy} data-testid={`${testid}-save`}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
